import { BOTTOM_BAR_ID, BOTTOM_BAR_OPEN_CLASS } from '@/components/bottom-bar/constants'
import { useEffect, useRef, useState } from 'react'

interface UIOverlayState {
	isDialogOpen: boolean
	isBottomBarOpen: boolean
}

/**
 * Hook to track UI overlay states (dialogs and bottom bar).
 * Detects actual modal dialogs (Dialog, AlertDialog), not popovers or dropdown menus.
 */
export function useUIOverlayState(): UIOverlayState {
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [isBottomBarOpen, setIsBottomBarOpen] = useState(false)
	const rafIdRef = useRef(0)

	useEffect(() => {
		const checkStates = () => {
			// Check for Radix dialogs (Dialog, AlertDialog, Sheet), not popovers or dropdown menus
			const openDialog = document.querySelector(
				'[data-state="open"][data-radix-dialog-content], [data-state="open"][data-radix-alert-dialog-content]',
			)
			setIsDialogOpen(!!openDialog)

			// Check for bottom bar open state
			const bottomBar = document.getElementById(BOTTOM_BAR_ID)
			setIsBottomBarOpen(bottomBar?.classList.contains(BOTTOM_BAR_OPEN_CLASS) ?? false)
		}

		// Debounce using RAF to coalesce rapid DOM changes into a single check per frame
		const debouncedCheck = () => {
			cancelAnimationFrame(rafIdRef.current)
			rafIdRef.current = requestAnimationFrame(checkStates)
		}

		// Initial check
		checkStates()

		// Single observer for both dialog and bottom bar state changes
		const observer = new MutationObserver(debouncedCheck)
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'data-state'],
		})

		return () => {
			observer.disconnect()
			cancelAnimationFrame(rafIdRef.current)
		}
	}, [])

	return { isDialogOpen, isBottomBarOpen }
}
